import {ActivatedRoute, NavigationEnd, ParamMap, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter, skip, takeUntil, takeWhile} from 'rxjs/operators';
import {Paginator} from '../pagination/paginator';
import {SnackbarService} from '../../services/snackbar/snackbar.service';
import {ListFilterType} from '../../types/list-filter-type';
import {UserActivityService} from '../../services/user-activity/user-activity.service';
import {TableSorter} from '../table-sorter/table-sorter';

export class ListFilters {
  values: { [name: string]: any } = {};

  private path = '';
  private storageKey = '';
  private subscription: Subscription | null = null;
  private onChangeEvent: () => void = () => {};

  constructor(
    private definitions: { [name: string]: ListFilterType },
    private paginator: Paginator | null,
    private tableSorter: TableSorter | null,
    private route: ActivatedRoute,
    private router: Router,
    private snackbar: SnackbarService,
    private userActivity: UserActivityService | null,
  ) {
    this.reset();
  }

  init(onChange: () => void): void {
    this.onChangeEvent = onChange;
    this.path = this.getCurrentPath();
    this.storageKey = 'list-filters:' + this.path;

    if (this.tableSorter) {
      const sort = this.getSavedSort();
      if (sort && sort.name && sort.order) {
        if (this.tableSorter.sortName !== sort.name) {
          this.tableSorter.sortName = sort.name;
        }
        if (this.tableSorter.sortOrder !== sort.order) {
          this.tableSorter.sortOrder = sort.order;
        }
      }
    }

    const saved = this.getSavedParams();
    const redirect = saved !== null && Object.keys(this.route.snapshot.queryParams).length === 0;

    if (redirect) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: saved,
        replaceUrl: true,
      }).then();
    }

    const leftPage = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      filter(() => this.getCurrentPath() !== this.path),
    );

    this.subscription = this.route.queryParamMap.pipe(
      skip(redirect ? 1 : 0),
      takeWhile(() => this.getCurrentPath() === this.path),
      takeUntil(leftPage),
    ).subscribe((params: ParamMap) => {
      this.parseParams(params);
      this.userActivity?.reportFilters(this.toParams());
      onChange();
    });
  }

  destroy(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  onFilterChange(): void {
    this.paginator?.reset();
    this.apply();
  }

  saveFilters(): void {
    const data: any = {
      filters: this.toQueryParams(),
      sort: null
    };

    if (this.tableSorter) {
      data.sort = {
        name: this.tableSorter.sortName,
        order: this.tableSorter.sortOrder
      };
    }

    localStorage.setItem(this.storageKey, JSON.stringify(data));
    this.snackbar.success('Filters have been saved');
  }

  clearFilters(): void {
    localStorage.removeItem(this.storageKey);
    this.paginator?.reset();
    this.tableSorter?.reset();
    this.reset();
    this.apply();
  }

  apply(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.toQueryParams(),
    }).then((redirect) => {
      if (!redirect) {
        this.onChangeEvent();
      }
    });
  }

  isEmpty(): boolean {
    return Object.keys(this.toQueryParams()).length === 0;
  }

  toParams(): { [name: string]: any } {
    const params: { [name: string]: any } = {};

    for (const name of Object.keys(this.definitions)) {
      const value = this.values[name];

      if (this.definitions[name] === 'boolean') {
        params[name] = value;
      } else if (this.hasValue(value)) {
        params[name] = value;
      }
    }

    return params;
  }

  private toQueryParams(): { [name: string]: any } {
    const params: { [name: string]: any } = {};

    for (const name of Object.keys(this.definitions)) {
      const value = this.values[name];

      if (this.definitions[name] === 'boolean' ? value === true : this.hasValue(value)) {
        params[name] = value;
      }
    }

    return params;
  }

  private hasValue(value: any): boolean {
    return Array.isArray(value) ? value.length > 0 : (value !== null && value !== '');
  }

  private reset(): void {
    for (const name of Object.keys(this.definitions)) {
      this.values[name] = this.getDefaultValue(this.definitions[name]);
    }
  }

  private getDefaultValue(type: ListFilterType): any {
    if (type.endsWith('[]')) {
      return [];
    }

    switch (type) {
      case 'string':
        return '';
      case 'boolean':
        return false;
      default:
        return null;
    }
  }

  private parseParams(params: ParamMap): void {
    for (const name of Object.keys(this.definitions)) {
      const type = this.definitions[name];

      if (type.endsWith('[]')) {
        const values = params.getAll(name);
        this.values[name] = type === 'number[]' ? values.map(Number) : [...values];
        continue;
      }

      const value = params.get(name);
      if (value === null) {
        this.values[name] = this.getDefaultValue(type);
      } else if (type === 'number') {
        this.values[name] = Number(value);
      } else if (type === 'boolean' || type === 'boolean?') {
        this.values[name] = value === 'true' || value === '1';
      } else {
        this.values[name] = value;
      }
    }
  }

  private getSavedParams(): { [name: string]: any } | null {
    const saved = localStorage.getItem(this.storageKey);
    if (!saved) {
      return null;
    }

    try {
      const data = JSON.parse(saved);
      return Object.keys(data.params.filters).length > 0 ? data.params.filters : null;
    } catch {
      return null;
    }
  }

  private getSavedSort(): { name: string | null; order: string | null } | null {
    const saved = localStorage.getItem(this.storageKey);
    if (!saved) {
      return null;
    }

    try {
      const data = JSON.parse(saved);
      return data.sort ? data.sort : null;
    } catch {
      return null;
    }
  }

  private getCurrentPath(): string {
    return this.router.url.split('?')[0];
  }
}
